import { useEffect, useState } from 'react';
import type { Tenant, Product } from '../lib/database.types';
import type { TenantFeatures } from '../lib/superAdmin';
import { gateFeature, isEntitled } from '../lib/entitlements';
import { loadMenu, type Menu } from '../lib/menu';
import { formatPrice } from '../lib/cart';
import { useCart } from '../hooks/useCart';
import ProductModal from './ProductModal';
import Checkout from './Checkout';
import CartView from './CartView';
import SlotPage from './SlotPage';
import StoreHero from './StoreHero';
import StoreInfo from './StoreInfo';
import StoreHeader from './StoreHeader';
import StoreFooter from './StoreFooter';
import BestSellers from './BestSellers';
import TrustBand from './TrustBand';
import LoyaltyCard from './LoyaltyCard';
import Confirmation from './Confirmation';
import CategoryGrid, { type CategoryCard } from './CategoryGrid';
import CtaBand from './CtaBand';
import InstallBanner from './InstallBanner';
import { placeOrder, saveCheckoutConsent, type CheckoutState, type DeliveryConfig } from '../lib/checkout';
import { hoursArrayToMap, slotKey, type SlotConfig, type TimeSlot } from '../lib/slots';
import { parseOrderPoint } from '../lib/orderPoint';
import { parseDeliveryZones } from '../lib/deliveryZones';
import { requestPayment, successUrlFor } from '../lib/payment';
import { shortTime } from '../lib/ticket';
import type { CartLine } from '../lib/cart';

/**
 * Vue client (US-503/505) — design porté du legacy Food Kourt v8.
 * Page unique : header de marque → hero → infos & accès → menu (cartes produit).
 * Tout est piloté par le tenant ; seuls couleur + logo changent d'un resto à l'autre.
 */
export default function MenuView({ tenant, features = {} }: { tenant: Tenant; features?: TenantFeatures }) {
  const [menu, setMenu] = useState<Menu | null>(null);
  const [selected, setSelected] = useState<Product | null>(null);
  const [editing, setEditing] = useState<{ index: number; line: CartLine } | null>(null);
  const [view, setView] = useState<'menu' | 'cart' | 'slot' | 'checkout' | 'loyalty' | 'confirmation'>('menu');
  // Créneau choisi sur la page dédiée « Heure de retrait » (étape avant le checkout).
  const [pickedSlot, setPickedSlot] = useState<TimeSlot | null>(null);
  const [note, setNote] = useState(''); // remarque saisie dans le panier (parité legacy)
  const [confirmed, setConfirmed] = useState<{
    orderNumber: string;
    totalCents: number;
    pickupTime: string | null;
    mode: 'pickup' | 'delivery';
    paymentMethod: 'online' | 'onsite' | null;
  } | null>(null);
  const [paying, setPaying] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const cart = useCart();
  const currency = tenant.currency;
  // Point de commande (kiosque QR) : `?point=table-3` → joint à la commande + ticket cuisine.
  // Gâté par l'entitlement `kiosk` : si Lil & Co le désactive, le paramètre est ignoré.
  const orderPoint = isEntitled(features, 'kiosk') ? parseOrderPoint(window.location.search) : null;

  useEffect(() => {
    let cancelled = false;
    loadMenu(tenant.id).then((m) => {
      if (!cancelled) setMenu(m);
    });
    return () => {
      cancelled = true;
    };
  }, [tenant.id]);

  // Entitlements effectifs (config resto ET autorisation Lil & Co). Rétro-compatible :
  // sans flag tenant_features, ces valeurs sont identiques à la config native du resto.
  const deliveryActive = gateFeature(features, 'delivery', tenant.delivery_enabled);
  const loyaltyActive = gateFeature(features, 'loyalty', tenant.loyalty_enabled);
  const onlinePaymentActive = gateFeature(features, 'online_payment', tenant.stripe_charges_enabled);

  const delivery: DeliveryConfig = {
    enabled: deliveryActive,
    feeCents: 0,
    minCents: tenant.delivery_min_cents ?? 0,
  };
  // Zones de livraison (parité C5) — vide si le tenant n'en a pas (livraison simple).
  const deliveryZones = parseDeliveryZones(tenant.delivery_zones);

  const slotConfig: SlotConfig = {
    hours: hoursArrayToMap(tenant.opening_hours ?? []) ?? {
      0: [],
      1: [],
      2: [],
      3: [],
      4: [],
      5: [],
      6: [],
    },
    stepMin: tenant.slot_step_min ?? undefined,
    prepMin: tenant.prep_time_min ?? undefined,
    capacity: tenant.slot_capacity ?? undefined,
    closures: (tenant.closures ?? [])
      .map((c) => (typeof c === 'string' ? c : c?.date ?? ''))
      .filter((s): s is string => Boolean(s)),
  };

  const confirm = async (state: CheckoutState) => {
    setOrderError(null);
    setPaying(true);
    // Frais/minimum effectifs (zone choisie le cas échéant) pour la commande réelle.
    const effDelivery: DeliveryConfig =
      state.deliveryFeeCents !== undefined
        ? { enabled: true, feeCents: state.deliveryFeeCents, minCents: state.deliveryMinCents ?? 0 }
        : delivery;
    const result = await placeOrder({
      tenantId: tenant.id,
      lines: cart.lines,
      mode: state.mode,
      delivery: effDelivery,
      customerName: state.customerName,
      email: state.email,
      phone: state.phone,
      slot: state.slot,
      paymentMethod: state.paymentMethod ?? undefined,
      // En livraison, on préfixe l'adresse dans la remarque (visible cuisine/livreur) —
      // la table orders n'a pas (encore) de colonne d'adresse dédiée.
      note:
        state.mode === 'delivery' && (state.deliveryStreet ?? '').trim()
          ? `🛵 Livraison : ${(state.deliveryStreet ?? '').trim()}, ${(state.deliveryPostal ?? '').trim()} ${(state.deliveryCity ?? '').trim()}`.trim() +
            (state.note ? ` — ${state.note}` : '')
          : state.note,
      orderPoint,
      promoCode: state.promoCode,
      // Opt-in WhatsApp coché au checkout → sans lui, aucune notification n'est envoyée.
      whatsappConsent: state.whatsappConsent !== false,
      // Acceptation EXPRESSE des CGV : tracée sur la commande, c'est la preuve opposable.
      termsAccepted: state.termsAccepted === true,
    });
    if (!result) {
      setPaying(false);
      setOrderError("La commande n'a pas pu être enregistrée. Merci de réessayer.");
      return;
    }

    // RGPD : enregistre les consentements marketing pour le CRM (best-effort, non bloquant).
    void saveCheckoutConsent(tenant.id, state.email, !!state.marketingConsent, !!state.networkConsent);

    // Paiement en ligne : on tente la redirection PSP (Stripe Checkout hébergé).
    if (state.paymentMethod === 'online') {
      const pay = await requestPayment({
        tenantId: tenant.id,
        amountCents: result.totalCents,
        orderNumber: result.orderNumber,
        successUrl: successUrlFor(window.location.origin, tenant.slug, result.orderNumber),
      });
      if (pay.mode === 'redirect') {
        cart.clear();
        window.location.href = pay.paymentUrl;
        return;
      }
      // PSP indisponible / non configuré (ou Stripe Elements inline non branché) :
      // la commande est bien enregistrée, on bascule sur la confirmation (à régler).
    }

    cart.clear();
    setConfirmed({
      orderNumber: result.orderNumber,
      totalCents: result.totalCents,
      pickupTime: shortTime(result.pickupTime),
      mode: state.mode,
      paymentMethod: state.paymentMethod,
    });
    setPaying(false);
    setView('confirmation');
  };

  // Navigation depuis le header : on revient toujours sur la vue menu, puis on défile
  // vers l'ancre voulue (after-render, comme le legacy).
  const goToAnchor = (id: string | null) => {
    setView('menu');
    if (!id) {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 60);
      return;
    }
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }), 60);
  };

  const header = (
    <StoreHeader
      tenant={tenant}
      cartCount={cart.count}
      cartSubtotal={cart.subtotal}
      currency={currency}
      onHome={() => goToAnchor(null)}
      onMenu={() => goToAnchor('menu')}
      onInfos={() => goToAnchor('infos')}
      onCart={() => setView('cart')}
      onLoyalty={loyaltyActive ? () => setView('loyalty') : undefined}
    />
  );

  const footer = <StoreFooter tenant={tenant} onHome={() => goToAnchor(null)} onMenu={() => goToAnchor('menu')} />;

  if (!menu) {
    return (
      <>
        {header}
        <p className="mx-auto max-w-5xl px-5 py-10 opacity-70">Chargement du menu…</p>
      </>
    );
  }

  const modalProduct: Product | null =
    selected ??
    (editing?.line.productId
      ? Object.values(menu.productsByCategory)
          .flat()
          .find((p) => p.id === editing.line.productId) ?? null
      : null);
  const closeModal = () => {
    setSelected(null);
    setEditing(null);
  };
  const submitModal = (line: CartLine) => {
    if (editing) cart.update(editing.index, line);
    else cart.add(line);
  };
  const modal = modalProduct && (
    <ProductModal
      product={modalProduct}
      options={menu.optionsByProduct[modalProduct.id] ?? []}
      currency={currency}
      initialSelections={editing?.line.selections}
      editing={!!editing}
      onAdd={submitModal}
      onClose={closeModal}
    />
  );

  if (view === 'checkout') {
    return (
      <>
        {header}
        <div className="mx-auto max-w-6xl px-5 py-8 pb-28">
          <Checkout
            lines={cart.lines}
            delivery={delivery}
            currency={currency}
            onConfirm={confirm}
            busy={paying}
            error={orderError}
            zones={deliveryZones}
            stripeActive={onlinePaymentActive}
            initialSlot={pickedSlot ? slotKey(pickedSlot) : null}
            initialPickedSlot={pickedSlot}
            onEditSlot={() => setView('slot')}
            initialNote={note}
            loyaltyActive={loyaltyActive}
            tenantId={tenant.id}
            loyaltyThreshold={tenant.loyalty_threshold}
            loyaltyReward={tenant.loyalty_reward_label}
            restaurantName={tenant.name}
          />
        </div>
        <InstallBanner tenant={tenant} active />
      </>
    );
  }

  if (view === 'cart') {
    return (
      <>
        {header}
        <div className="mx-auto max-w-6xl px-5 py-8 pb-28">
          <CartView
            lines={cart.lines}
            currency={currency}
            subtotal={cart.subtotal}
            onEdit={(i) => setEditing({ index: i, line: cart.lines[i] })}
            onRemove={cart.removeAt}
            onBack={() => setView('menu')}
            onCheckout={() => setView('slot')}
            note={note}
            onNoteChange={setNote}
          />
        </div>
        <InstallBanner tenant={tenant} active />
        {modal}
      </>
    );
  }

  if (view === 'slot') {
    return (
      <>
        {header}
        <div className="mx-auto max-w-6xl px-5 py-8 pb-28">
          <SlotPage
            lines={cart.lines}
            slotConfig={slotConfig}
            currency={currency}
            phone={tenant.phone1}
            deliveryEnabled={delivery.enabled}
            initial={pickedSlot}
            onBack={() => setView('cart')}
            onContinue={(slot) => {
              setPickedSlot(slot);
              setView('checkout');
            }}
          />
        </div>
        <InstallBanner tenant={tenant} active />
      </>
    );
  }

  if (view === 'loyalty') {
    return (
      <>
        {header}
        <div className="mx-auto flex max-w-5xl justify-center px-5 py-8">
          <LoyaltyCard tenant={tenant} onBack={() => setView('menu')} />
        </div>
        {footer}
      </>
    );
  }

  if (view === 'confirmation' && confirmed) {
    return (
      <>
        {header}
        <Confirmation
          tenant={tenant}
          orderNumber={confirmed.orderNumber}
          totalCents={confirmed.totalCents}
          currency={currency}
          pickupTime={confirmed.pickupTime}
          mode={confirmed.mode}
          paymentMethod={confirmed.paymentMethod}
          reviewsEnabled={isEntitled(features, 'reviews')}
          onBackToMenu={() => {
            setConfirmed(null);
            setView('menu');
          }}
        />
        {footer}
        <InstallBanner tenant={tenant} active />
      </>
    );
  }

  const visibleCategories = menu.categories.filter(
    (c) => (menu.productsByCategory[c.id] ?? []).length > 0,
  );
  const allProducts = visibleCategories.flatMap((c) => menu.productsByCategory[c.id] ?? []);
  const featured = allProducts.filter((p) => p.is_featured);
  const categoryCards: CategoryCard[] = visibleCategories.map((c) => ({
    id: c.id,
    name: c.name,
    emoji: c.emoji,
    count: (menu.productsByCategory[c.id] ?? []).length,
  }));
  const scrollToCategory = (id: string) =>
    document.getElementById(`cat-${id}`)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="fade-in">
      {header}
      {tenant.is_open === false && (
        <div className="bg-red-600 px-4 py-2 text-center text-sm font-semibold text-white">
          ⛔ Restaurant momentanément fermé — commandes indisponibles pour l'instant.
        </div>
      )}
      {orderPoint && (
        <div
          className="px-4 py-2 text-center text-sm font-semibold text-white"
          style={{ background: 'var(--brand-primary)' }}
        >
          📍 Commande pour : {orderPoint}
        </div>
      )}
      <StoreHero tenant={tenant} categoryNames={visibleCategories.map((c) => c.name)} />

      <TrustBand tenant={tenant} />

      <BestSellers
        products={featured}
        currency={currency}
        onSelect={setSelected}
        onSeeAll={() => goToAnchor('menu')}
      />

      <CategoryGrid
        categories={categoryCards}
        productCount={allProducts.length}
        onPick={scrollToCategory}
      />

      <div className="mx-auto max-w-7xl px-4 lg:px-6">
        {/* Nav catégories sticky */}
        <div
          id="menu"
          className="scroll-hide sticky top-16 z-30 -mx-4 flex gap-2 overflow-x-auto border-b border-black/5 bg-[#fafaf7]/95 px-4 py-3 backdrop-blur lg:top-20"
        >
          {visibleCategories.map((c) => (
            <button
              key={c.id}
              onClick={() =>
                document.getElementById(`cat-${c.id}`)?.scrollIntoView({ behavior: 'smooth' })
              }
              className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
            >
              {c.emoji && <span>{c.emoji}</span>}
              {c.name}
            </button>
          ))}
        </div>

        {visibleCategories.map((c) => (
          <section key={c.id} id={`cat-${c.id}`} className="scroll-mt-28 pt-8">
            <h2 className="display-font mb-5 text-3xl font-black sm:text-4xl">
              {c.emoji} {c.name}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(menu.productsByCategory[c.id] ?? []).map((p) => (
                <ProductCard key={p.id} product={p} currency={currency} onSelect={setSelected} />
              ))}
            </div>
          </section>
        ))}

        <div className="h-16" />
      </div>

      <StoreInfo tenant={tenant} onOrder={() => goToAnchor('menu')} />

      <CtaBand prepMin={tenant.prep_time_min ?? 15} onOrder={() => goToAnchor('menu')} />

      {footer}

      {cart.count > 0 && (
        <div className="sticky bottom-0 z-40 border-t border-black/5 bg-white/95 px-5 py-3 backdrop-blur">
          <button
            onClick={() => setView('cart')}
            className="mx-auto flex w-full max-w-5xl items-center justify-center rounded-2xl py-3.5 text-base font-bold text-white shadow-lg"
            style={{ background: 'var(--brand-primary)' }}
          >
            Voir le panier ({cart.count}) — {formatPrice(cart.subtotal, currency)}
          </button>
        </div>
      )}

      {modal}
    </div>
  );
}

/** Carte produit (grille) — image/emoji, badge populaire, nom, description, prix. */
function ProductCard({
  product,
  currency,
  onSelect,
}: {
  product: Product;
  currency: 'EUR' | 'CHF';
  onSelect: (p: Product) => void;
}) {
  return (
    <button
      onClick={() => onSelect(product)}
      className="group flex flex-col overflow-hidden rounded-2xl border border-black/5 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl"
    >
      <div
        className="relative flex h-40 items-center justify-center overflow-hidden text-6xl"
        style={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--brand-primary) 10%, white), color-mix(in srgb, var(--brand-primary) 22%, white))',
        }}
      >
        {/* Visuel dégradé toujours présent dessous ; la photo le couvre si elle charge. */}
        <span className="transition-transform duration-500 group-hover:scale-110">{product.emoji || '🍽️'}</span>
        {product.image_url && (
          <img
            src={product.image_url}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        {product.is_featured && (
          <span className="absolute left-3 top-3 rounded-full bg-yellow-400 px-2.5 py-1 text-xs font-extrabold text-yellow-900">
            ★ Populaire
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-bold leading-tight">{product.name}</h3>
        {product.description && (
          <p className="mt-1 line-clamp-2 min-h-[40px] text-sm text-slate-500">
            {product.description}
          </p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xl font-extrabold" style={{ color: 'var(--brand-primary)' }}>
            {formatPrice(product.base_price_cents, currency)}
          </span>
          <span
            className="rounded-full px-3 py-1.5 text-xs font-bold text-white"
            style={{ background: 'var(--brand-primary)' }}
          >
            + Choisir
          </span>
        </div>
      </div>
    </button>
  );
}
